import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle, X, ExternalLink } from 'lucide-react';

/**
 * Inline "How to use" affordance for a feature/process. Renders a small button
 * that opens a popover with a short intro + numbered steps, and (optionally) a
 * link to the full docs section.
 *
 * Props:
 *   title:   string  — the process name
 *   intro:   string  — one-line summary (optional)
 *   steps:   string[] — ordered how-to steps
 *   section: string  — Help section id for the "Open full guide" link (optional)
 *   testId:  string  — data-testid prefix
 */
export default function ProcessHelp({ title, intro, steps = [], section, testId = 'process-help' }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`How to use ${title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg border text-xs transition-colors ${open ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
      >
        <HelpCircle className="w-4 h-4" /> How to use
      </button>

      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label={`${title} — how to use`}
          data-testid={`${testId}-popover`}
          className="absolute right-0 top-11 w-[380px] max-w-[calc(100vw-2rem)] glass-card rounded-xl shadow-2xl border border-border/50 z-[130] overflow-hidden"
        >
          <div className="flex items-start justify-between gap-2 p-4 pb-2">
            <h3 className="text-sm font-semibold text-foreground" data-testid={`${testId}-title`}>{title}</h3>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 p-0.5 flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {intro ? <p className="px-4 -mt-1 mb-2 text-xs text-muted-foreground leading-relaxed">{intro}</p> : null}
          <ol className="px-4 pb-3 space-y-1.5 list-decimal list-inside">
            {steps.map((s, i) => (
              <li key={i} className="text-xs text-foreground/85 leading-relaxed">{s}</li>
            ))}
          </ol>
          {section ? (
            <div className="flex items-center justify-end px-4 py-2.5 border-t border-border/40 bg-muted/20">
              <a
                href={`/help?s=${encodeURIComponent(section)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid={`${testId}-full`}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              >
                Open full guide <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
